import { Calendar } from 'lucide-react';
import { KanbanCard as KanbanCardShell } from '@/ui/primitives';
import { cn } from '@/lib/utils';
import {
  CATEGORIA_CLASS,
  TRIAGEM_DOT,
  TRIAGEM_LABEL,
  categoriaDaVerificacao,
} from '../categorias';
import { type Card as CardData, type Temperatura } from '../schemas';

function fmtBRL(n: number) {
  return n.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
}

const iniciaisDe = (nome: string) =>
  nome
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');

interface Props {
  card: CardData;
  onClick: () => void;
}

const TEMP_CLASS: Record<Temperatura, string> = {
  quente: 'bg-[rgba(220,38,38,0.10)] text-[var(--danger)]',
  morna: 'bg-[rgba(234,179,8,0.14)] text-[#a16207] dark:text-[#facc15]',
  fria: 'bg-[rgba(0,113,227,0.10)] text-[var(--info)]',
};

const CHIP = 'inline-flex items-center rounded-full px-2 py-0.5 text-[0.7rem] font-medium';

export function CrmCard({ card, onClick }: Props) {
  const categoria = categoriaDaVerificacao(card.verificacao);
  const responsavel = card.comercial_nome || card.analista_nome || null;
  const valorFechado = card.valor_fechado != null ? Number(card.valor_fechado) : null;
  const valorProposta = card.proposta_valor != null ? Number(card.proposta_valor) : null;
  const valorEtq =
    valorFechado && valorFechado > 0
      ? { tipo: 'fechado' as const, valor: valorFechado }
      : valorProposta && valorProposta > 0
        ? { tipo: 'proposta' as const, valor: valorProposta }
        : null;

  return (
    <KanbanCardShell onClick={onClick} className="cursor-pointer">
      {/* Cliente + fazenda. A denominação chega a 91 caracteres: quebra livre,
          sem corte — o nome próprio do sítio costuma estar no fim da string. */}
      <div className="flex flex-col gap-1">
        <div className="text-sm font-semibold leading-tight text-text">
          {card.cliente_nome || card.cliente_cpf}
        </div>
        {card.imovel_denominacao && (
          <div className="text-[0.78rem] leading-[1.35] text-text-2">
            {card.imovel_denominacao}
          </div>
        )}
      </div>

      {/* Triagem (esteira: ponto + fundo neutro) e categoria (checagem: fundo
          tingido) são famílias visuais distintas de propósito — respondem
          perguntas diferentes e não podem se confundir. */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className={cn(CHIP, 'gap-1.5 bg-surface2 text-text-2')}>
          <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', TRIAGEM_DOT[card.frente])} />
          {TRIAGEM_LABEL[card.frente]}
        </span>
        {categoria && <span className={cn(CHIP, CATEGORIA_CLASS[categoria])}>{categoria}</span>}
        {card.temperatura && (
          <span className={cn(CHIP, 'capitalize', TEMP_CLASS[card.temperatura])}>
            {card.temperatura}
          </span>
        )}
        {valorEtq && (
          <span
            className={cn(
              CHIP,
              'tabular-nums',
              valorEtq.tipo === 'fechado'
                ? 'bg-[rgba(16,185,129,0.12)] text-[var(--ok)]'
                : 'bg-[var(--accent-soft)] text-accent',
            )}
            title={valorEtq.tipo === 'fechado' ? 'Valor fechado' : 'Valor da proposta'}
          >
            {fmtBRL(valorEtq.valor)}
          </span>
        )}
      </div>

      {/* Rodapé: dias na fase + responsável. Sem responsável fica VAZIO — 3 de
          cada 4 cards não têm, e o texto repetido viraria ruído na coluna. */}
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-[0.7rem] text-text-muted">
          <Calendar size={13} strokeWidth={1.8} />
          {card.dias_na_coluna}d na fase
        </span>
        {responsavel && (
          <span className="flex min-w-0 items-center gap-1.5" title={responsavel}>
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-surface3 text-[0.55rem] font-semibold text-text-2">
              {iniciaisDe(responsavel)}
            </span>
            <span className="truncate text-[0.7rem] font-medium text-text-2">
              {responsavel.split(' ')[0]}
            </span>
          </span>
        )}
      </div>
    </KanbanCardShell>
  );
}
